// Copyright (c) Bentley Systems
import { SVGProps, useEffect, useState } from "react";
import { animated, useSprings } from "@react-spring/web";
import { AnimatedIconProps } from "./AnimatedIconProps";

const BottomRectangle = (props: React.JSX.IntrinsicAttributes & SVGProps<SVGGElement>) => {
  return (
    <g {...props}>
      <rect
        width="42"
        height="42"
        fill="#67b01a"
        fillOpacity="60%"
        stroke="#fff"
        strokeOpacity="0.6"
        strokeWidth="0.5"
        rx="3"
        ry="3"
        transform="translate(5 5)"
       />
      <path
        transform="translate(5)"
        fill="#fff"
        d="M20.699 21.708a.507.507 0 00-.502-.52h-1.674a.518.518 0 00-.486.386.502.502 0 00-.485-.385h-1.657a.493.493 0 00-.502.502v.084H13.3V13.02c0-.552-.469-1.021-1.021-1.021h-2.176c-.552 0-1.004.469-1.004 1.021v.435c-1.205.084-2.143.586-2.143 1.222v11.8H5.25l.033.05c.034.084.118.234.252.435.184.302.468.72.853 1.205l.117.118H20.03l-.134-.185a1.814 1.814 0 01-.234-1.49c.017-.033.017-.1.033-.133.1-.234.235-.452.419-.653l1.473-1.456h-.887v-2.661zm-5.306 4.77h-3.565V23.95h3.565v2.527zM11.81 23.38v-.485h.519v.485h-.519zm.686 0v-.485h.435v.485h-.435zm.57 0v-.485h.451v.485h-.452zm.602 0v-.485h.452v.485h-.452zm.62 0v-.485h.451v.485h-.452zm1.104 0h-.536v-.485h.536v.485zm0-.636h-3.565v-8.067c0-.636-.937-1.138-2.143-1.222v-.435c0-.234.185-.418.419-.418h2.176c.234 0 .435.184.435.418v9.038c0 .168.117.285.284.285h2.394v.401zm3.883 3.733h-2.494v-2.059h2.494v2.059zM25.352 18.009c-1.774 0-3.549.351-3.549 1.004v5.138l.419-.435c.368-.335.82-.519 1.289-.519.351 0 .686.1.987.301l.502.335.1-.586c.185-.853.921-1.439 1.775-1.439h2.059v-2.795c0-.653-1.791-1.004-3.582-1.004zm-2.678 1.221c-.168.05-.352.134-.52.235v-.502c0-.1.202-.235.52-.352v.62zm.753-.167c-.2 0-.402.067-.603.117v-.602c.201-.067.402-.118.603-.151v.636zm.736-.117c-.217 0-.418.067-.602.084v-.62a6.02 6.02 0 01.602-.067v.603zm.72-.034c-.2 0-.401.017-.569.034v-.62c.168-.016.368-.016.57-.05v.636zm.737-.016h-.586v-.62h.586v.62zm.753.05c-.201-.017-.402-.034-.603-.034v-.636c.201.034.402.034.603.05v.62zm.72.084c-.185-.017-.385-.067-.603-.084v-.603c.218.017.418.034.603.067v.62zm.736.15c-.2-.05-.402-.1-.603-.117v-.636c.201.033.402.084.603.15v.603zm.67.285a2.368 2.368 0 00-.52-.235v-.619c.319.117.52.251.52.352v.502z"
       />
      <path
        transform="translate(5)"
        fill="#0E630A"
        d="M36.75 33.491v-2.996l-2.293-.469a7.91 7.91 0 00-.402-.937l1.306-1.958-2.109-2.11-1.958 1.306a5.907 5.907 0 00-.954-.418l-.469-2.276h-2.996l-.452 2.276a4.85 4.85 0 00-.954.418l-1.958-1.305-2.11 2.109 1.306 1.958c-.167.301-.3.62-.401.954l-2.293.469v2.979l2.293.469c.1.318.25.652.401.954l-1.305 1.958 2.109 2.109 1.975-1.306c.301.151.602.268.937.385l.469 2.31h2.996l.468-2.31c.302-.117.62-.234.921-.401l1.975 1.322 2.109-2.126-1.306-1.975c.151-.284.268-.619.385-.92l2.31-.469zm-8.385 1.674a3.19 3.19 0 01-3.18-3.18c0-1.758 1.439-3.197 3.18-3.197 1.74 0 3.197 1.44 3.197 3.197a3.182 3.182 0 01-3.197 3.18z"
       />
    </g>
  );
}

const TopRectangle = (props: React.JSX.IntrinsicAttributes & SVGProps<SVGGElement>) => {
  return (
    <g {...props}>
      <rect
        width="42"
        height="42"
        fill="#87cd1e"
        stroke="#83c444"
        strokeWidth="0.5"
        rx="3"
        ry="3"
        transform="translate(0)"
       />
    </g>
  );
}

const AdministrationAnim = (props: AnimatedIconProps) => {
  const [toggle, setToggle] = useState(false);

  useEffect(() => {
    if (props.isFocused !== undefined) {setToggle(props.isFocused);}
  }, [props]);

  const rectangles = [
    <TopRectangle key="rectangle1" />,
    <BottomRectangle key="rectangle2" />,
  ];

  const springs = useSprings(
    rectangles.length, // the number of springs to create
    rectangles.map((el, index) => {
      return {
        transform: toggle
          ? `translate3d(0px, 0px, 0px)`
          : `translate3d(${index === 1 ? -5 : 5}px, 0px, 0px)`,
      };
    })
  );

  const animatedRectangles = springs.map((animatedStyle, index) => (
    // use `animated.g` instead of `g` as the `animatedStyle`
    // is a special react-spring flavour of the `style` property
    <animated.g
      key={index}
      style={animatedStyle} // apply the animated style
    >
      {rectangles[index]}
    </animated.g>
  ));

  return (
    <div
      onMouseOver={() => {
        if (props.isFocused === undefined) {setToggle(true);}
      }}
      onMouseOut={() => {
        if (props.isFocused === undefined) {setToggle(false);}
      }}
    >
      <svg
        style={{ height: "100%", width: "100%" }}
        xmlns="http://www.w3.org/2000/svg"
        width="47"
        height="47"
        viewBox="0 0 47 47"
      >
        {animatedRectangles}
      </svg>
    </div>
  );
};

export default AdministrationAnim;
